import { useState, useCallback, useContext, useMemo, useEffect, useRef } from 'react'
import ApplicationContext from '../context/ApplicationContext'
import SearchLocationContext from '../context/SearchLocationContext'
import FormatUtilities from '../utilities/FormatUtilities'
import LocationUtilities from '../utilities/LocationUtilities'

const useSearchLocation = () => {
    const [applicationData] = useContext(ApplicationContext)
    const [localizations] = useState(applicationData.localizations)
    const [searchLocationData, setSearchLocationData] = useContext(SearchLocationContext)
    const abortControllerRef = useRef(null)
    const [stableLocationList, setStableLocationList] = useState([])
    const lastRequestRef = useRef('')

    // Helper function to generate a unique signature for request deduplication
    const getRequestSignature = searchBody => {
        return JSON.stringify({
            lat: searchBody.latitude,
            lng: searchBody.longitude,
            radius: searchBody.radius,
            sortBy: searchBody.sortBy,
            unitType: searchBody.unitType,
            filters: searchBody.filters,
            eventSearchFilters: searchBody.eventSearchFilters
        })
    }

    const getUnitTypeOptions = () => {
        return [
            {
                title: localizations.miles,
                value: 'MI',
            },
            {
                title: localizations.kilometers,
                value: 'KM',
            },
        ]
    }
    const [unitTypeOptions] = useState(getUnitTypeOptions)
    const [unitTypeValue, setUnitTypeValue] = useState(FormatUtilities.formatDefaultValue(applicationData.unitType, unitTypeOptions))
    const getSortOptions = () => {
        return [
            {
                title: localizations.default,
                value: 'default',
            },
            {
                title: localizations.distance,
                value: 'distance',
            },
        ]
    }
    const [sortOptions] = useState(getSortOptions)
    let getRadiusOptions = () => {
        return [
            {
                title: `200 ${unitTypeValue} ${localizations.radius}`,
                value: 200,
                zoom: 8,
            },
            {
                title: `100 ${unitTypeValue} ${localizations.radius}`,
                value: 100,
                zoom: 9,
            },
            {
                title: `75 ${unitTypeValue} ${localizations.radius}`,
                value: 75,
                zoom: 10,
            },
            {
                title: `50 ${unitTypeValue} ${localizations.radius}`,
                value: 50,
                zoom: 11,
            },
            {
                title: `25 ${unitTypeValue} ${localizations.radius}`,
                value: 25,
                zoom: 12,
            },
            {
                title: `10 ${unitTypeValue} ${localizations.radius}`,
                value: 10,
                zoom: 14,
            },
            {
                title: `5 ${unitTypeValue} ${localizations.radius}`,
                value: 5,
                zoom: 15,
            },
        ]
    }
    const [radiusOptions, setRadiusOptions] = useState(getRadiusOptions)
    const [radiusValue, setRadiusValue] = useState(FormatUtilities.formatDefaultValue(applicationData.radius, radiusOptions))
    const [sortValue, setSortValue] = useState(FormatUtilities.formatDefaultValue(applicationData.sort, sortOptions))
    const [mapCenter] = useState(applicationData.mapCenter)
    const hideSort = useMemo(() => applicationData?.hideSort, [applicationData])

    useEffect(() => {
        const queryParams = {}

        if (searchLocationData.mapCenter.lat && searchLocationData.mapCenter.lng) {
            queryParams.lat = searchLocationData.mapCenter.lat
            queryParams.lng = searchLocationData.mapCenter.lng
        }

        if (searchLocationData.radius) {
            queryParams.r = searchLocationData.radius
        }

        if (!searchLocationData.hideSort && searchLocationData.sortBy) {
            queryParams.s = searchLocationData.sortBy
        }

        if (searchLocationData.unitType) {
            queryParams.u = searchLocationData.unitType
        }

        if (searchLocationData.defaultZoom) {
            queryParams.z = searchLocationData.defaultZoom
        }

        const newParams = new URLSearchParams(queryParams)

        if (searchLocationData.appliedFilters?.length) {
            searchLocationData.appliedFilters.forEach(filter => {
                newParams.append('f', filter.groupName !== '' ? filter.groupName : filter.title)
            })
        }

        // Update the URL in the browser's address bar without reloading the page
        window.history.replaceState({}, '', `?${newParams}`)
    }, [searchLocationData])

    useEffect(() => {
        setRadiusOptions(getRadiusOptions)
    }, [unitTypeValue])

    useEffect(() => {
        setRadiusValue(FormatUtilities.formatDefaultValue(searchLocationData.radius, radiusOptions))
    }, [radiusOptions])

    // Update stable location list only when we have actual new data and not loading
    useEffect(() => {
        if (!searchLocationData.locationsLoading && searchLocationData.locationList) {
            setStableLocationList(searchLocationData.locationList)
        }
    }, [searchLocationData.locationsLoading, searchLocationData.locationList])

    // Cleanup effect to cancel any pending requests on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])

    const handleMapCenterUpdate = useCallback(
        option => {
            if (
                option.lat === searchLocationData.mapCenter.lat &&
                option.lng === searchLocationData.mapCenter.lng &&
                option.zoom === searchLocationData.defaultZoom
            ) {
                return
            }

            // Cancel previous request if it exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }

            // Create new AbortController for this request
            abortControllerRef.current = new AbortController()

            setSearchLocationData(prevState => {
                return {
                    ...prevState,
                    locationsLoading: true,
                    mapCenter: {
                        lat: option.lat,
                        lng: option.lng,
                    },
                }
            })

            const searchBody = {
                ...LocationUtilities.generateSearchBody(searchLocationData),
                latitude: option.lat,
                longitude: option.lng,
            }

            if (option.zoom) {
                const output = radiusOptions.reduce((prev, curr) =>
                    Math.abs(curr.zoom - option.zoom) < Math.abs(prev.zoom - option.zoom) ? curr : prev
                )
                searchBody.radius = output.value

                setSearchLocationData(prevState => {
                    return {
                        ...prevState,
                        radius: output.value,
                        defaultZoom: option.zoom,
                    }
                })
            }

            if (searchLocationData.disableRoamingRequest) {
                setSearchLocationData(prevState => {
                    return {
                        ...prevState,
                        disableRoamingRequest: false,
                    }
                })
            }

            // Check if this is a duplicate request
            const requestSignature = getRequestSignature(searchBody)
            if (lastRequestRef.current === requestSignature) {
                // Duplicate request - don't make API call
                setSearchLocationData(prevState => ({
                    ...prevState,
                    locationsLoading: false
                }))
                return
            }
            lastRequestRef.current = requestSignature

            LocationUtilities.postSearchLocations(searchBody, abortControllerRef.current.signal)
                .then(response => {
                    // Only update if this request wasn't cancelled
                    if (!abortControllerRef.current?.signal.aborted) {
                        setSearchLocationData(prevState => {
                            return {
                                ...prevState,
                                locationList: response.data.data,
                                locationsLoading: false,
                            }
                        })
                    }
                })
                .catch(error => {
                    // Only handle errors if request wasn't cancelled
                    if (error.name !== 'AbortError' && !abortControllerRef.current?.signal.aborted) {
                        setSearchLocationData(prevState => {
                            return {
                                ...prevState,
                                locationsLoading: false,
                                error: true,
                            }
                        })
                    }
                })
        },
        [mapCenter, searchLocationData, radiusOptions]
    )

    const handleSearch = useCallback(
        (position, address) => {
            if (typeof position.lat === 'number' && typeof position.lng === 'number') {
                // Cancel previous request if it exists
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort()
                }

                // Create new AbortController for this request
                abortControllerRef.current = new AbortController()

                setSearchLocationData(previousState => {
                    return {
                        ...previousState,
                        locationsLoading: true,
                        address: address,
                        mapCenter: {
                            lat: position.lat,
                            lng: position.lng,
                        },
                        disableRoamingRequest: true,
                    }
                })

                const searchBody = {
                    ...LocationUtilities.generateSearchBody(searchLocationData),
                    latitude: position.lat,
                    longitude: position.lng,
                }

                LocationUtilities.postSearchLocations(searchBody, abortControllerRef.current.signal)
                    .then(response => {
                        // Only update if this request wasn't cancelled
                        if (!abortControllerRef.current?.signal.aborted) {
                            setSearchLocationData(prevState => {
                                return {
                                    ...prevState,
                                    locationList: response.data.data,
                                    locationsLoading: false,
                                }
                            })
                        }
                    })
                    .catch(error => {
                        // Only handle errors if request wasn't cancelled
                        if (error.name !== 'AbortError' && !abortControllerRef.current?.signal.aborted) {
                            setSearchLocationData(prevState => {
                                return {
                                    ...prevState,
                                    locationsLoading: false,
                                    error: true,
                                }
                            })
                        }
                    })
            }
        },
        [searchLocationData]
    )

    const handleRadiusChange = useCallback(
        option => {
            if (option.title !== radiusValue) {
                // Cancel previous request if it exists
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort()
                }

                // Create new AbortController for this request
                abortControllerRef.current = new AbortController()

                setRadiusValue(option.title)
                setSearchLocationData(prevState => {
                    return {
                        ...prevState,
                        locationsLoading: true,
                        radius: option.value,
                        defaultZoom: option.zoom,
                    }
                })

                const searchBody = {
                    ...LocationUtilities.generateSearchBody(searchLocationData),
                    radius: option.value,
                }

                LocationUtilities.postSearchLocations(searchBody, abortControllerRef.current.signal)
                    .then(response => {
                        // Only update if this request wasn't cancelled
                        if (!abortControllerRef.current?.signal.aborted) {
                            setSearchLocationData(prevState => {
                                return {
                                    ...prevState,
                                    locationList: response?.data?.data,
                                    locationsLoading: false,
                                }
                            })
                        }
                    })
                    .catch(error => {
                        // Only handle errors if request wasn't cancelled
                        if (error.name !== 'AbortError' && !abortControllerRef.current?.signal.aborted) {
                            setSearchLocationData(prevState => {
                                return {
                                    ...prevState,
                                    locationsLoading: false,
                                    error: true,
                                }
                            })
                        }
                    })
            }
        },
        [radiusValue, searchLocationData]
    )

    const handleSortChange = useCallback(
        option => {
            if (option.title !== sortValue) {
                // Cancel previous request if it exists
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort()
                }

                // Create new AbortController for this request
                abortControllerRef.current = new AbortController()

                setSortValue(option.title)
                setSearchLocationData(prevState => {
                    return {
                        ...prevState,
                        locationsLoading: true,
                        sortBy: option.value,
                        disableRoamingRequest: true,
                    }
                })

                const searchBody = {
                    ...LocationUtilities.generateSearchBody(searchLocationData),
                    sortBy: option.value,
                }

                LocationUtilities.postSearchLocations(searchBody, abortControllerRef.current.signal)
                    .then(response => {
                        // Only update if this request wasn't cancelled
                        if (!abortControllerRef.current?.signal.aborted) {
                            setSearchLocationData(prevState => {
                                return {
                                    ...prevState,
                                    locationList: response?.data?.data,
                                    locationsLoading: false,
                                }
                            })
                        }
                    })
                    .catch(error => {
                        // Only handle errors if request wasn't cancelled
                        if (error.name !== 'AbortError' && !abortControllerRef.current?.signal.aborted) {
                            setSearchLocationData(prevState => {
                                return {
                                    ...prevState,
                                    locationsLoading: false,
                                    error: true,
                                }
                            })
                        }
                    })
            }
        },
        [sortValue, searchLocationData]
    )

    const handleUnitChange = useCallback(
        option => {
            if (option.title !== unitTypeValue) {
                // Cancel previous request if it exists
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort()
                }

                // Create new AbortController for this request
                abortControllerRef.current = new AbortController()

                setUnitTypeValue(option.title)
                setSearchLocationData(prevState => {
                    return {
                        ...prevState,
                        locationsLoading: true,
                        unitType: option.value,
                        disableRoamingRequest: true,
                    }
                })

                const searchBody = {
                    ...LocationUtilities.generateSearchBody(searchLocationData),
                    unitType: option.value,
                }

                LocationUtilities.postSearchLocations(searchBody, abortControllerRef.current.signal)
                    .then(response => {
                        // Only update if this request wasn't cancelled
                        if (!abortControllerRef.current?.signal.aborted) {
                            setSearchLocationData(prevState => {
                                return {
                                    ...prevState,
                                    locationList: response?.data?.data,
                                    locationsLoading: false,
                                }
                            })
                        }
                    })
                    .catch(error => {
                        // Only handle errors if request wasn't cancelled
                        if (error.name !== 'AbortError' && !abortControllerRef.current?.signal.aborted) {
                            setSearchLocationData(prevState => {
                                return {
                                    ...prevState,
                                    locationsLoading: false,
                                    error: true,
                                }
                            })
                        }
                    })
            }
        },
        [unitTypeValue, searchLocationData]
    )

    return {
        radiusOptions,
        sortOptions,
        unitTypeOptions,
        radiusValue,
        sortValue,
        unitTypeValue,
        handleRadiusChange,
        handleSortChange,
        hideSort,
        handleMapCenterUpdate,
        handleSearch,
        handleUnitChange,
        stableLocationList,
    }
}

export { useSearchLocation }
